import { useState, useRef, useEffect } from "react";

interface SupplierRating {
  name: string;
  grade: "A+" | "A" | "B" | "C";
  score: number;
  delivery: number;
  quality: number;
  price: number;
  response: number;
  risk: "低" | "中" | "高";
}

interface MaintenanceRisk {
  device: string;
  model: string;
  riskScore: number;
  lastMaintain: string;
  nextMaintain: string;
  overdue: number;
  faultRate: number;
  riskLevel: "低" | "中" | "高" | "极高";
}

const supplierRatings: SupplierRating[] = [
  { name: "金卡智能", grade: "A+", score: 96, delivery: 98, quality: 97, price: 92, response: 99, risk: "低" },
  { name: "西门子", grade: "A+", score: 94, delivery: 95, quality: 99, price: 85, response: 96, risk: "低" },
  { name: "霍尼韦尔", grade: "A", score: 91, delivery: 93, quality: 96, price: 82, response: 94, risk: "低" },
  { name: "重庆耐德", grade: "A", score: 88, delivery: 90, quality: 91, price: 88, response: 85, risk: "低" },
  { name: "中核苏阀", grade: "B", score: 74, delivery: 72, quality: 78, price: 90, response: 68, risk: "中" },
  { name: "联塑管业", grade: "A", score: 89, delivery: 91, quality: 88, price: 92, response: 87, risk: "低" },
];

const maintenanceRisks: MaintenanceRisk[] = [
  { device: "超声波流量计", model: "DN50-US", riskScore: 88, lastMaintain: "2025-07-20", nextMaintain: "2026-03-09", overdue: 32, faultRate: 8.5, riskLevel: "极高" },
  { device: "调压器", model: "RTZ-50/0.4", riskScore: 72, lastMaintain: "2025-09-01", nextMaintain: "2026-09-01", overdue: 0, faultRate: 5.2, riskLevel: "高" },
  { device: "球阀DN50", model: "Q41F-16C", riskScore: 58, lastMaintain: "2026-01-15", nextMaintain: "2026-07-15", overdue: 0, faultRate: 2.1, riskLevel: "中" },
  { device: "IC卡燃气表", model: "G2.5-IC", riskScore: 32, lastMaintain: "2026-01-15", nextMaintain: "2026-06-01", overdue: 0, faultRate: 0.8, riskLevel: "低" },
  { device: "可燃气体探测器", model: "JT-B2000", riskScore: 45, lastMaintain: "2025-11-20", nextMaintain: "2026-05-01", overdue: 0, faultRate: 1.5, riskLevel: "中" },
  { device: "PE管DN110", model: "SDR11-110", riskScore: 22, lastMaintain: "2025-12-01", nextMaintain: "2026-12-01", overdue: 0, faultRate: 0.3, riskLevel: "低" },
];

const inventoryHealthData = [
  { category: "计量设备", health: 82, stock: 1336, minStock: 800, value: 1256000, trend: "stable" },
  { category: "管材配件", health: 95, stock: 2800, minStock: 1000, value: 190400, trend: "up" },
  { category: "阀门", health: 48, stock: 320, minStock: 500, value: 49920, trend: "down" },
  { category: "安全设备", health: 88, stock: 560, minStock: 200, value: 235200, trend: "stable" },
  { category: "调压设备", health: 72, stock: 45, minStock: 20, value: 83250, trend: "down" },
  { category: "通信设备", health: 91, stock: 180, minStock: 50, value: 126000, trend: "up" },
];

function HealthScoreRing({ score, size = 80 }: { score: number; size?: number }) {
  const color = score >= 80 ? "#22c55e" : score >= 60 ? "#f59e0b" : "#ef4444";
  const r = (size - 12) / 2;
  const circ = 2 * Math.PI * r;
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#f1f5f9" strokeWidth="6" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="6"
          strokeDasharray={`${(score / 100) * circ} ${circ}`} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-bold text-gray-800" style={{ fontSize: size * 0.22 }}>{score}</span>
        <span className="text-gray-400" style={{ fontSize: size * 0.12 }}>分</span>
      </div>
    </div>
  );
}

function RiskMatrix() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    const pad = 40;
    const cw = w - pad * 2;
    const ch = h - pad * 2;

    // Background quadrants
    const quads = [
      { x: pad, y: pad, w: cw / 2, h: ch / 2, color: "rgba(239,68,68,0.08)", label: "高概率高影响" },
      { x: pad + cw / 2, y: pad, w: cw / 2, h: ch / 2, color: "rgba(245,158,11,0.08)", label: "低概率高影响" },
      { x: pad, y: pad + ch / 2, w: cw / 2, h: ch / 2, color: "rgba(245,158,11,0.08)", label: "高概率低影响" },
      { x: pad + cw / 2, y: pad + ch / 2, w: cw / 2, h: ch / 2, color: "rgba(34,197,94,0.08)", label: "低概率低影响" },
    ];
    quads.forEach(q => {
      ctx.fillStyle = q.color;
      ctx.fillRect(q.x, q.y, q.w, q.h);
      ctx.fillStyle = "rgba(100,116,139,0.4)";
      ctx.font = "9px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(q.label, q.x + q.w / 2, q.y + q.h / 2);
    });

    // Grid lines
    ctx.strokeStyle = "rgba(0,0,0,0.08)";
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(pad + cw / 2, pad); ctx.lineTo(pad + cw / 2, pad + ch); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(pad, pad + ch / 2); ctx.lineTo(pad + cw, pad + ch / 2); ctx.stroke();

    // Axes
    ctx.strokeStyle = "#94a3b8";
    ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(pad, pad); ctx.lineTo(pad, pad + ch); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(pad, pad + ch); ctx.lineTo(pad + cw, pad + ch); ctx.stroke();

    // Axis labels
    ctx.fillStyle = "#64748b";
    ctx.font = "10px sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("故障概率 →", pad + cw / 2, h - 8);
    ctx.save();
    ctx.translate(12, pad + ch / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText("影响程度 →", 0, 0);
    ctx.restore();

    // Plot devices
    const devices = [
      { name: "超声波流量计", prob: 0.85, impact: 0.9, color: "#ef4444" },
      { name: "调压器", prob: 0.52, impact: 0.85, color: "#f97316" },
      { name: "球阀DN50", prob: 0.35, impact: 0.6, color: "#f59e0b" },
      { name: "探测器", prob: 0.28, impact: 0.75, color: "#f59e0b" },
      { name: "IC卡表", prob: 0.12, impact: 0.4, color: "#22c55e" },
      { name: "PE管", prob: 0.08, impact: 0.3, color: "#22c55e" },
    ];

    devices.forEach(d => {
      const x = pad + d.prob * cw;
      const y = pad + (1 - d.impact) * ch;
      const r = 10;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fillStyle = d.color + "30";
      ctx.fill();
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.strokeStyle = d.color;
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.fillStyle = d.color;
      ctx.font = "bold 8px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(d.name.slice(0, 3), x, y + 3);
    });
  }, []);
  return <canvas ref={canvasRef} width={380} height={220} className="w-full rounded-xl" />;
}

export default function MaterialAIDeep() {
  const [activeTab, setActiveTab] = useState<"health" | "supplier" | "risk" | "report">("health");
  const [aiReport, setAiReport] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [exportModal, setExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState("PDF");
  const [exporting, setExporting] = useState(false);
  const [exportDone, setExportDone] = useState(false);

  const overallHealth = Math.round(inventoryHealthData.reduce((s, d) => s + d.health, 0) / inventoryHealthData.length);

  const generateReport = () => {
    setAiGenerating(true);
    setAiReport("");
    const prompt = aiPrompt || "综合分析库存健康度、供应商评级和维护风险";
    const text = `【AI材料管理深度分析报告】
分析维度：${prompt}
生成时间：2026-04-10 14:30 | 数据截止：2026-04-10

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
一、库存健康度综合评估
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
• 综合健康度评分：${overallHealth}分（良好）
• 健康品类：4类（计量设备82分、管材配件95分、安全设备88分、通信设备91分）
• 预警品类：2类（阀门48分⚠️、调压设备72分）
• 重点关注：球阀DN50库存320件，低于安全库存500件，健康度仅48分
  → 建议立即启动采购，预计补货周期7-10天，建议采购量≥500件
• 库存总值：¥194万，较上月增长3.2%

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
二、供应商评级分析
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
• A+级供应商：金卡智能（96分）、西门子（94分）——建议优先合作
• A级供应商：霍尼韦尔（91分）、联塑管业（89分）、重庆耐德（88分）
• B级供应商：中核苏阀（74分）——近期交货延迟率上升至8%，建议季度评估
• 供应商风险预警：中核苏阀响应速度评分仅68分，建议引入备选供应商
• 建议：对中核苏阀发出改进通知，限期3个月提升至A级，否则降低采购份额

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
三、维护风险矩阵分析
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
• 极高风险：超声波流量计DN50-US（风险评分88）
  → 已超期维护32天，故障率8.5%，位于风险矩阵高概率高影响区
  → 建议：本周内完成年度维护，预计费用¥2.8万
• 高风险：调压器RTZ-50/0.4（风险评分72）
  → 近3个月故障率上升15%，建议全面检查该批次设备
• 中风险：球阀DN50、可燃气体探测器（风险评分45-58）
  → 按计划维护，重点关注使用年限超过5年的设备
• 低风险：IC卡燃气表、PE管（风险评分22-32）——正常维护即可

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
四、AI优化建议（优先级排序）
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
P0（立即执行）：
  1. 安排超声波流量计年度维护（本周内）
  2. 启动球阀DN50补货采购（本周内）

P1（本月内）：
  3. 对调压器RTZ-50/0.4进行全面检查
  4. 向中核苏阀发出供应商改进通知

P2（本季度）：
  5. 建立动态库存预警机制（三级阈值）
  6. 引入RFID资产追踪系统
  7. 对高频消耗品建立长期框架协议

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
五、下季度预算建议
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
• 采购预算：¥85万（较上季度+12%）
• 维护预算：¥12.5万
• 质保续签：¥8.5万
• 合计：¥106万`;

    let i = 0;
    const interval = setInterval(() => {
      if (i < text.length) {
        setAiReport(prev => prev + text[i]);
        i++;
      } else {
        clearInterval(interval);
        setAiGenerating(false);
      }
    }, 15);
  };

  const handleExport = () => {
    setExporting(true);
    setTimeout(() => {
      setExporting(false);
      setExportDone(true);
      setTimeout(() => { setExportDone(false); setExportModal(false); }, 2000);
    }, 2500);
  };

  return (
    <div className="space-y-4">
      {/* Tab switcher */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        {[
          { key: "health", label: "库存健康度", icon: "ri-heart-pulse-line" },
          { key: "supplier", label: "供应商评级", icon: "ri-star-line" },
          { key: "risk", label: "维护风险矩阵", icon: "ri-radar-line" },
          { key: "report", label: "AI报告生成", icon: "ri-robot-2-line" },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key as typeof activeTab)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${activeTab === t.key ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
          >
            <i className={t.icon}></i>{t.label}
          </button>
        ))}
      </div>

      {/* Health Score Tab */}
      {activeTab === "health" && (
        <div className="space-y-4">
          {/* Overall score */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center gap-6">
              <HealthScoreRing score={overallHealth} size={100} />
              <div className="flex-1">
                <h3 className="text-base font-bold text-gray-900 mb-1">库存综合健康度</h3>
                <p className="text-sm text-gray-500 mb-3">基于库存量、周转率、预警状态综合评估</p>
                <div className="flex items-center gap-4">
                  {[
                    { label: "健康品类", value: "4类", color: "text-green-600", bg: "bg-green-50" },
                    { label: "预警品类", value: "2类", color: "text-amber-600", bg: "bg-amber-50" },
                    { label: "库存总值", value: "¥194万", color: "text-[#E8531A]", bg: "bg-orange-50" },
                  ].map((s, i) => (
                    <div key={i} className={`${s.bg} rounded-xl px-4 py-2 text-center`}>
                      <div className={`text-lg font-bold ${s.color}`}>{s.value}</div>
                      <div className="text-xs text-gray-500">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Category health grid */}
          <div className="grid grid-cols-3 gap-3">
            {inventoryHealthData.map((item, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-gray-800">{item.category}</span>
                  <div className="flex items-center gap-1">
                    <i className={`text-xs ${item.trend === "up" ? "ri-arrow-up-line text-green-500" : item.trend === "down" ? "ri-arrow-down-line text-red-500" : "ri-subtract-line text-gray-400"}`}></i>
                    <span className={`text-xs font-bold ${item.health >= 80 ? "text-green-600" : item.health >= 60 ? "text-amber-600" : "text-red-600"}`}>{item.health}分</span>
                  </div>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2 mb-3">
                  <div
                    className="h-2 rounded-full transition-all"
                    style={{ width: `${item.health}%`, backgroundColor: item.health >= 80 ? "#22c55e" : item.health >= 60 ? "#f59e0b" : "#ef4444" }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  <div className="text-gray-400">库存量</div>
                  <div className="text-gray-700 font-medium text-right">{item.stock.toLocaleString()}</div>
                  <div className="text-gray-400">安全库存</div>
                  <div className={`font-medium text-right ${item.stock < item.minStock ? "text-red-600" : "text-gray-700"}`}>{item.minStock.toLocaleString()}</div>
                  <div className="text-gray-400">库存价值</div>
                  <div className="text-[#E8531A] font-medium text-right">¥{(item.value / 10000).toFixed(1)}万</div>
                </div>
                {item.stock < item.minStock && (
                  <div className="mt-2 bg-red-50 rounded-lg px-2 py-1 flex items-center gap-1">
                    <i className="ri-error-warning-line text-red-500 text-xs"></i>
                    <span className="text-xs text-red-600">库存不足，需补货</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Supplier Rating Tab */}
      {activeTab === "supplier" && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-900">供应商综合评级</h3>
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <span className="bg-green-50 text-green-600 px-2 py-0.5 rounded-full font-medium">A+ 优质</span>
                <span className="bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full font-medium">A 良好</span>
                <span className="bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full font-medium">B 一般</span>
                <span className="bg-red-50 text-red-600 px-2 py-0.5 rounded-full font-medium">C 差</span>
              </div>
            </div>
            <div className="divide-y divide-gray-50">
              {supplierRatings.map((s, i) => (
                <div key={i} className="px-5 py-4">
                  <div className="flex items-center gap-4 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                      <i className="ri-building-4-line text-[#E8531A] text-lg"></i>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-gray-900">{s.name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${s.grade === "A+" ? "bg-green-50 text-green-600" : s.grade === "A" ? "bg-blue-50 text-blue-600" : s.grade === "B" ? "bg-amber-50 text-amber-600" : "bg-red-50 text-red-600"}`}>{s.grade}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${s.risk === "低" ? "bg-green-50 text-green-600" : s.risk === "中" ? "bg-amber-50 text-amber-600" : "bg-red-50 text-red-600"}`}>风险{s.risk}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-gray-900">{s.score}</div>
                      <div className="text-xs text-gray-400">综合评分</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    {[
                      { label: "交货准时率", value: s.delivery },
                      { label: "产品质量", value: s.quality },
                      { label: "价格竞争力", value: s.price },
                      { label: "响应速度", value: s.response },
                    ].map((item, j) => (
                      <div key={j}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-gray-400">{item.label}</span>
                          <span className={`font-medium ${item.value >= 90 ? "text-green-600" : item.value >= 75 ? "text-amber-600" : "text-red-600"}`}>{item.value}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-1.5">
                          <div className="h-1.5 rounded-full" style={{ width: `${item.value}%`, backgroundColor: item.value >= 90 ? "#22c55e" : item.value >= 75 ? "#f59e0b" : "#ef4444" }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  {s.grade === "B" && (
                    <div className="mt-3 bg-amber-50 rounded-lg px-3 py-2 flex items-center gap-2">
                      <i className="ri-robot-2-line text-amber-600 text-sm"></i>
                      <span className="text-xs text-amber-700">AI建议：发出改进通知，限期3个月提升至A级，否则降低采购份额</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Risk Matrix Tab */}
      {activeTab === "risk" && (
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-7 space-y-4">
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <h3 className="text-sm font-bold text-gray-900 mb-4">维护风险矩阵（故障概率 × 影响程度）</h3>
              <RiskMatrix />
              <div className="flex items-center gap-4 mt-3 text-xs">
                {[
                  { color: "#ef4444", label: "极高风险" },
                  { color: "#f97316", label: "高风险" },
                  { color: "#f59e0b", label: "中风险" },
                  { color: "#22c55e", label: "低风险" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                    <span className="text-gray-500">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="col-span-5 space-y-3">
            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <h3 className="text-sm font-bold text-gray-900 mb-3">设备维护风险排行</h3>
              <div className="space-y-3">
                {maintenanceRisks.sort((a, b) => b.riskScore - a.riskScore).map((item, i) => (
                  <div key={i} className={`rounded-xl p-3 ${item.riskLevel === "极高" ? "bg-red-50" : item.riskLevel === "高" ? "bg-orange-50" : item.riskLevel === "中" ? "bg-amber-50" : "bg-gray-50"}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-1.5 py-0.5 rounded font-bold ${item.riskLevel === "极高" ? "bg-red-100 text-red-600" : item.riskLevel === "高" ? "bg-orange-100 text-orange-600" : item.riskLevel === "中" ? "bg-amber-100 text-amber-600" : "bg-gray-100 text-gray-500"}`}>{item.riskLevel}</span>
                        <span className="text-xs font-semibold text-gray-800">{item.device}</span>
                      </div>
                      <span className={`text-sm font-bold ${item.riskLevel === "极高" ? "text-red-600" : item.riskLevel === "高" ? "text-orange-600" : item.riskLevel === "中" ? "text-amber-600" : "text-gray-500"}`}>{item.riskScore}</span>
                    </div>
                    <div className="w-full bg-white/60 rounded-full h-1.5 mb-2">
                      <div className="h-1.5 rounded-full" style={{ width: `${item.riskScore}%`, backgroundColor: item.riskLevel === "极高" ? "#ef4444" : item.riskLevel === "高" ? "#f97316" : item.riskLevel === "中" ? "#f59e0b" : "#22c55e" }} />
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 text-xs">
                      <div className="text-gray-500">下次维护：<span className="text-gray-700 font-medium">{item.nextMaintain}</span></div>
                      <div className="text-gray-500">故障率：<span className={`font-medium ${item.faultRate > 5 ? "text-red-600" : "text-gray-700"}`}>{item.faultRate}%</span></div>
                      {item.overdue > 0 && (
                        <div className="col-span-2 text-red-600 font-medium">⚠️ 已超期 {item.overdue} 天</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Report Tab */}
      {activeTab === "report" && (
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-8 space-y-4">
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-robot-2-line text-[#E8531A] text-lg"></i>
                <h3 className="text-sm font-bold text-gray-800">AI材料管理深度分析报告</h3>
                <span className="ml-auto text-xs text-gray-400">支持自定义分析维度</span>
              </div>

              {/* Dimension selector */}
              <div className="flex flex-wrap gap-2 mb-4">
                {["库存健康度分析", "供应商评级分析", "维护风险评估", "采购预算建议", "全面综合分析"].map(dim => (
                  <button
                    key={dim}
                    onClick={() => setAiPrompt(dim)}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors cursor-pointer whitespace-nowrap ${aiPrompt === dim ? "border-[#E8531A] bg-orange-50 text-[#E8531A]" : "border-gray-200 text-gray-500 hover:border-[#E8531A]/50"}`}
                  >
                    {dim}
                  </button>
                ))}
              </div>

              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={aiPrompt}
                  onChange={e => setAiPrompt(e.target.value)}
                  placeholder="自定义分析要求（如：重点分析超声波流量计的维护风险和采购建议）..."
                  className="flex-1 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#E8531A]"
                />
                <button
                  onClick={generateReport}
                  disabled={aiGenerating}
                  className="flex items-center gap-1.5 bg-[#E8531A] text-white px-4 py-2.5 rounded-xl text-sm font-medium cursor-pointer hover:bg-[#d44a15] whitespace-nowrap disabled:opacity-70"
                >
                  <i className={`${aiGenerating ? "ri-loader-4-line animate-spin" : "ri-robot-2-line"}`}></i>
                  {aiGenerating ? "生成中..." : "生成报告"}
                </button>
              </div>

              {aiReport ? (
                <div className="bg-gray-50 rounded-xl p-4 max-h-96 overflow-y-auto">
                  <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">{aiReport}</pre>
                  {aiGenerating && <span className="inline-block w-1 h-3 bg-[#E8531A] animate-pulse ml-0.5"></span>}
                </div>
              ) : (
                <div className="bg-gray-50 rounded-xl p-10 text-center">
                  <i className="ri-robot-2-line text-gray-300 text-5xl mb-3 block"></i>
                  <p className="text-gray-400 text-sm">选择分析维度或输入自定义要求，AI将生成专业的材料管理分析报告</p>
                  <p className="text-gray-300 text-xs mt-1">包含库存健康度、供应商评级、维护风险矩阵、采购建议等</p>
                </div>
              )}

              {aiReport && !aiGenerating && (
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => setExportModal(true)}
                    className="flex items-center gap-1.5 bg-[#E8531A] text-white px-4 py-2 rounded-lg text-xs font-medium cursor-pointer hover:bg-[#d44a15] whitespace-nowrap"
                  >
                    <i className="ri-download-line"></i>导出报告
                  </button>
                  <button className="flex items-center gap-1.5 border border-gray-200 text-gray-600 px-3 py-2 rounded-lg text-xs cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                    <i className="ri-mail-line"></i>发送邮件
                  </button>
                  <button className="flex items-center gap-1.5 border border-gray-200 text-gray-600 px-3 py-2 rounded-lg text-xs cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                    <i className="ri-printer-line"></i>打印
                  </button>
                  <button onClick={() => { setAiReport(""); setAiPrompt(""); }} className="flex items-center gap-1.5 border border-gray-200 text-gray-600 px-3 py-2 rounded-lg text-xs cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                    <i className="ri-refresh-line"></i>重新生成
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="col-span-4 space-y-4">
            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <h4 className="text-sm font-semibold text-gray-800 mb-3">快速数据摘要</h4>
              <div className="space-y-3">
                <div className="bg-orange-50 rounded-xl p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-heart-pulse-line text-[#E8531A] text-sm"></i>
                    <span className="text-xs font-semibold text-[#E8531A]">库存健康度</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <HealthScoreRing score={overallHealth} size={48} />
                    <div className="text-xs text-gray-600">
                      <div>综合评分 <span className="font-bold text-gray-800">{overallHealth}分</span></div>
                      <div className="text-gray-400 mt-0.5">2类品类需关注</div>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 rounded-xl p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-star-line text-green-600 text-sm"></i>
                    <span className="text-xs font-semibold text-green-700">供应商评级</span>
                  </div>
                  <div className="space-y-1">
                    {[{ grade: "A+", count: 2 }, { grade: "A", count: 3 }, { grade: "B", count: 1 }].map((g, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <span className="text-gray-500">{g.grade}级</span>
                        <span className="font-bold text-gray-700">{g.count}家</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-red-50 rounded-xl p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-radar-line text-red-600 text-sm"></i>
                    <span className="text-xs font-semibold text-red-700">维护风险</span>
                  </div>
                  <div className="space-y-1">
                    {[
                      { level: "极高", count: 1, color: "text-red-600" },
                      { level: "高", count: 1, color: "text-orange-600" },
                      { level: "中", count: 2, color: "text-amber-600" },
                      { level: "低", count: 2, color: "text-green-600" },
                    ].map((r, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <span className="text-gray-500">{r.level}风险</span>
                        <span className={`font-bold ${r.color}`}>{r.count}类</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <h4 className="text-sm font-semibold text-gray-800 mb-3">报告模板</h4>
              <div className="space-y-2">
                {[
                  { label: "月度库存报告", icon: "ri-calendar-line" },
                  { label: "供应商季度评估", icon: "ri-building-4-line" },
                  { label: "设备维护计划", icon: "ri-tools-line" },
                  { label: "采购预算申请", icon: "ri-shopping-cart-2-line" },
                ].map((item, i) => (
                  <button
                    key={i}
                    onClick={() => { setAiPrompt(item.label); }}
                    className="w-full flex items-center gap-2 p-2.5 rounded-xl bg-gray-50 hover:bg-orange-50 text-xs text-gray-600 hover:text-[#E8531A] transition-colors cursor-pointer text-left"
                  >
                    <i className={`${item.icon} text-sm`}></i>
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {exportModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => !exporting && setExportModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-[400px]" onClick={e => e.stopPropagation()}>
            {exportDone ? (
              <div className="text-center py-4">
                <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-check-double-line text-green-500 text-3xl"></i>
                </div>
                <div className="text-base font-bold text-gray-900">导出成功！</div>
                <div className="text-xs text-gray-400 mt-1">报告已保存到下载文件夹</div>
              </div>
            ) : (
              <>
                <h3 className="text-base font-bold text-gray-900 mb-4">导出AI分析报告</h3>
                <div className="space-y-3 mb-5">
                  <div>
                    <label className="text-xs text-gray-500 mb-2 block">导出格式</label>
                    <div className="grid grid-cols-3 gap-2">
                      {["PDF", "Excel", "Word"].map(fmt => (
                        <button
                          key={fmt}
                          onClick={() => setExportFormat(fmt)}
                          className={`py-2.5 rounded-xl text-sm font-medium border-2 transition-colors cursor-pointer whitespace-nowrap ${exportFormat === fmt ? "border-[#E8531A] bg-orange-50 text-[#E8531A]" : "border-gray-100 text-gray-600"}`}
                        >
                          {fmt}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-2 block">包含内容</label>
                    <div className="space-y-2">
                      {["库存健康度评分", "供应商评级详情", "维护风险矩阵", "AI优化建议", "预算建议"].map((item, i) => (
                        <label key={i} className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" defaultChecked className="accent-[#E8531A]" />
                          <span className="text-xs text-gray-600">{item}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                {exporting ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                      <span>正在生成 {exportFormat} 报告...</span>
                      <span>75%</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div className="h-2 bg-[#E8531A] rounded-full animate-pulse" style={{ width: "75%" }} />
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <button onClick={() => setExportModal(false)} className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap">取消</button>
                    <button onClick={handleExport} className="flex-1 bg-[#E8531A] text-white py-2.5 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap">
                      <i className="ri-download-line mr-1"></i>导出 {exportFormat}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
